import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import T from "prop-types";
import { debounce, get, isEqual, xor } from "lodash";
import { Formik, FieldArray, Field } from "formik";
import { Col } from "react-bootstrap";
import { FormInput } from "MODULES/shared/components/Forms/FormikFormInput/FormInput";
import Select from "MODULES/shared/components/Forms/FormikFormInput/Select";
import { FormRow, Form as AppForm } from "MODULES/shared/components/Forms";
import { properCase } from "LIB/formatters/text";
import { LABOR_COST, PART_COST } from "MODULES/SalesOrders/constants";
import moment from "moment";
import {
  ACTION_REQUESTED,
  ACTIONS_REQUESTED,
  APPROVAL_STATUS,
  APPROVAL_STATUSES,
  AWAITING_TO_SHIP,
  ESTIMATED_LEAD_TIME,
  EVALUATION_CHARGE,
  INTERNAL_OUTRIGHT,
  ORIGINAL_EVALUATION,
  REPAIR_AND_HOLD,
  REPAIR_EVALUATION_RECEIVED,
  TRANSFER_COST,
  UNIT_RETURNED_TO_CUSTOMER
} from "MODULES/RepairOrders/constants";
import {
  CAAC,
  CONDITION,
  EASA,
  FAA,
  INSPECTED,
  INVENTORY,
  OTHER,
  OTHER_TAG_TYPE,
  OVERHAULED,
  REPAIR_EVALUATION_APPROVED,
  REPAIR_EVALUATION_REJECTED,
  REPAIRED,
  SERIAL_NUMBER,
  TAG_DATE,
  TCCA
} from "MODULES/inventories/constants";
import { toOptions } from "LIB/forms";
import { approvalStatuses } from "MODULES/RepairOrders/components/Detail/LineItems/FormSections/ActionRequested/ActionFormSection";
import { PART_NUMBER } from "MODULES/parts/constants";
import api from "APP_ROOT/api";
import { removeKeys } from "LIB/removeKey";
import { Warning } from "MODULES/shared/components";
import { FormikFooter } from "MODULES/shared/components/Forms/FormikForm/FormikFooter";
import { rounder } from "MODULES/shared/components/Forms/NumberInput/NumberInput";
import { CONDITIONS } from "APP_ROOT/constants";
import { EVALUATION_FEE } from "MODULES/RepairQuotes/constants";
import { CONDITION_TYPES } from "MODULES/Shipping/constants";
import FormikCheck from "MODULES/shared/components/Forms/FormikFormInput/FormikCheck";
import validate from "./validate";

function LineRow({
  idx,
  initialValues,
  itemsSelected,
  setFieldValue,
  values,
  toggleItem
}) {
  const item = `line_items[${idx}]`;
  const filteredOptions = approvalStatuses(
    get(initialValues, `${item}.${APPROVAL_STATUS}`),
    get(values, `${item}.${ORIGINAL_EVALUATION}`),
    get(values, `${item}.${ACTION_REQUESTED}`)
  );

  const approvalStatus = get(values, `${item}.${APPROVAL_STATUS}`);
  const other = get(values, `${item}.${OTHER}`);
  const prevApprovalStatusRef = useRef(approvalStatus);

  const tagRequired =
    ([OVERHAULED, INSPECTED, REPAIRED].includes(
      get(values, [item, INVENTORY, CONDITION].join("."))
    ) &&
      !get(values, `${item}.${REPAIR_AND_HOLD}`)) ||
    approvalStatus === REPAIR_EVALUATION_APPROVED;

  const parseValueToZero = value =>
    value === "" || value === undefined ? "0" : value;

  const formatValueFromZero = value =>
    value === "0" || value === 0 || value === undefined ? "" : value;

  useEffect(
    () => {
      // Only clear the fields if the approval status has changed from its previous state
      if (approvalStatus !== prevApprovalStatusRef.current) {
        setFieldValue(`line_items[${idx}].transfer_cost`, "");
      }
      // Update the ref to the current status for the next render
      prevApprovalStatusRef.current = approvalStatus;
      if (approvalStatus === "repair_evaluation_rejected") {
        setFieldValue(`line_items[${idx}].evaluation_charge`, "0.00");
      }
    },
    [approvalStatus, idx, setFieldValue]
  );

  return [
    <FormRow key={idx} className="striped">
      <Col md={1}>
        <input
          type="checkbox"
          className="radio-inline"
          checked={itemsSelected.includes(get(values, `${item}.id`))}
          onClick={() => toggleItem(get(values, `${item}.id`), idx)}
          style={{ marginLeft: 10, marginRight: 10 }}
        />
      </Col>
      <Col md={2}>
        <FormInput
          name={`line_items[${idx}].part.part_number`}
          label={properCase(PART_NUMBER)}
          disabled
        />
      </Col>
      <Col md={2}>
        <FormInput
          name={`line_items[${idx}].serial_number`}
          label={properCase(SERIAL_NUMBER)}
          disabled
        />
      </Col>
      <Col md={2}>
        <Select
          name={`line_items[${idx}].action_requested`}
          label={properCase(ACTION_REQUESTED)}
          options={toOptions(ACTIONS_REQUESTED)}
          disabled
        />
      </Col>
      <Col md={2}>
        <Select
          name={`line_items[${idx}].approval_status`}
          inputProps={{
            field: {
              onChange: ev => {
                if (ev.target.value === REPAIR_EVALUATION_REJECTED) {
                  setFieldValue(`line_items[${idx}].evaluation_charge`, 0);
                }
                setFieldValue(
                  `line_items[${idx}].approval_status`,
                  ev.target.value
                );
              }
            }
          }}
          label={properCase(APPROVAL_STATUS)}
          options={toOptions(filteredOptions)}
        />
      </Col>
      {approvalStatus !== REPAIR_EVALUATION_REJECTED && (
        <Col md={3}>
          <FormInput
            name={`line_items[${idx}].tag_date`}
            label={properCase(TAG_DATE)}
            isRequired={tagRequired}
            type="month"
          />
        </Col>
      )}
    </FormRow>,
    <FormRow>
      <Col md={2} mdOffset={1}>
        <FormInput
          name={`line_items[${idx}].original_evaluation`}
          label={properCase(ORIGINAL_EVALUATION)}
          step="0.01"
          type="number"
          isRequired={[
            REPAIR_EVALUATION_APPROVED,
            REPAIR_EVALUATION_RECEIVED
          ].includes(approvalStatus)}
        />
      </Col>
      {approvalStatus !== REPAIR_EVALUATION_REJECTED ? (
        [
          <Col md={2}>
            <FormInput
              name={`line_items[${idx}].part_cost`}
              label={properCase(PART_COST)}
              type="number"
              step="0.01"
              parse={parseValueToZero}
              format={formatValueFromZero}
              isRequired={approvalStatus === REPAIR_EVALUATION_APPROVED}
            />
          </Col>,
          <Col md={2}>
            <FormInput
              name={`line_items[${idx}].labor_cost`}
              label={properCase(LABOR_COST)}
              step="0.01"
              type="number"
              parse={parseValueToZero}
              format={formatValueFromZero}
              isRequired={approvalStatus === REPAIR_EVALUATION_APPROVED}
            />
          </Col>
        ]
      ) : (
        <Col md={2}>
          <FormInput
            name={`line_items[${idx}].evaluation_charge`}
            label={properCase(EVALUATION_FEE)}
            step="0.01"
            type="number"
            isRequired
          />
        </Col>
      )}
      {approvalStatus === REPAIR_EVALUATION_APPROVED && (
        <Col md={2}>
          <FormInput
            name={`line_items[${idx}].transfer_cost`}
            label={properCase(TRANSFER_COST)}
            step="0.01"
            type="number"
            defaultValue="0.00"
          />
        </Col>
      )}
      <Col md={2}>
        <FormInput
          name={`line_items[${idx}].estimated_lead_time`}
          label={properCase(ESTIMATED_LEAD_TIME)}
          type="number"
        />
      </Col>
    </FormRow>,
    <FormRow>
      <Col md={2} mdOffset={1}>
        <Select
          name={`line_items[${idx}].inventory.condition`}
          label="Inventory Condition"
          options={toOptions(CONDITION_TYPES)}
          disabled={approvalStatus !== REPAIR_EVALUATION_APPROVED}
        />
      </Col>
    </FormRow>,
    <FormRow>
      <Col md={11} mdOffset={1}>
        <div className="form-group" style={{ cursor: "pointer" }}>
          <label className="control-label" htmlFor="tag_type">
            Release Type{" "}
            {approvalStatus === REPAIR_EVALUATION_APPROVED && (
              <sup className="small text-danger">*</sup>
            )}
          </label>
          <div
            id="tag_type"
            className="btn-group btn-group-justified"
            data-toggle="buttons"
          >
            <FormikCheck
              label={FAA.toUpperCase()}
              name={`line_items[${idx}].faa`}
            />
            <FormikCheck
              label={EASA.toUpperCase()}
              name={`line_items[${idx}].easa`}
            />
            <FormikCheck
              label={TCCA.toUpperCase()}
              name={`line_items[${idx}].tcca`}
            />
            <FormikCheck
              label={CAAC.toUpperCase()}
              name={`line_items[${idx}].caac`}
            />
            <FormikCheck
              label={OTHER.toUpperCase()}
              name={`line_items[${idx}].other`}
            />
          </div>
        </div>
        <Field name={`line_items[${idx}].Tag Type`}>
          {({ field, form }) => {
            const jsxErrors =
              get(form.errors, field.name) &&
              get(form.errors, field.name).map(m => (
                <span className="text-danger" key={m}>
                  {m}
                </span>
              ));

            return jsxErrors || null;
          }}
        </Field>
      </Col>
    </FormRow>,
    <FormRow>
      {other && (
        <Col md={2} mdOffset={1}>
          <FormInput
            name={`line_items[${idx}].${OTHER_TAG_TYPE}`}
            label={properCase(OTHER_TAG_TYPE)}
          />
        </Col>
      )}
    </FormRow>
  ];
}

class Form extends React.Component {
  state = {
    itemsSelected: this.props.itemsSelected
  };

  toggleItem = (setField, initialValues) => (id, item) => {
    const itemsSelected = xor(this.state.itemsSelected, [id]);

    if (!itemsSelected.includes(id)) {
      [
        CONDITION,
        TAG_DATE,
        APPROVAL_STATUS,
        PART_COST,
        LABOR_COST,
        TRANSFER_COST,
        ESTIMATED_LEAD_TIME,
        ORIGINAL_EVALUATION
      ].forEach(f => {
        const field = `line_items[${item}].${f}`;

        setField(field, get(initialValues, field) || null);
      });
    }

    this.setState({
      itemsSelected
    });
  };

  handleTopLevelFormChange = (
    field,
    values,
    initialValues,
    setFieldValue
  ) => ev => {
    const { includeCancelled = false } = this.props;
    if (!ev.target) return;
    const val = ev.target.value;
    values.line_items
      .filter(l => includeCancelled || !l.cancelled)
      .forEach((l, idx) => {
        if (this.state.itemsSelected.includes(l.id)) {
          if (field === APPROVAL_STATUS) {
            this.setState({ topLevelApprovalStatus: val });

            const filteredOptions = approvalStatuses(
              get(initialValues, `line_items[${idx}].${APPROVAL_STATUS}`),
              get(values, `line_items[${idx}].${ORIGINAL_EVALUATION}`),
              get(`line_items[${idx}].${ACTION_REQUESTED}`)
            );

            if (
              Object.keys(filteredOptions).includes(val) &&
              !get(values, `line_items[${idx}].${INTERNAL_OUTRIGHT}`)
            ) {
              setFieldValue(`line_items[${idx}].${field}`, val);
            }
          } else if (field === "inventory.condition") {
            if (
              get(values, `line_items[${idx}].${APPROVAL_STATUS}`) ===
              REPAIR_EVALUATION_APPROVED
            ) {
              setFieldValue(`line_items[${idx}].${field}`, val);
            }
          } else {
            const formattedVal = [
              PART_COST,
              LABOR_COST,
              TRANSFER_COST,
              ORIGINAL_EVALUATION
            ].includes(field)
              ? rounder(val)
              : val;
            if (
              field === TRANSFER_COST &&
              (formattedVal === "" ||
                formattedVal === undefined ||
                formattedVal === null)
            ) {
              return setFieldValue(`line_items[${idx}].${field}`, 0.0);
            }
            setFieldValue(`line_items[${idx}].${field}`, formattedVal);
          }
        }
        return null;
      });
  };

  handleAmountFormChange = (ev, field) => {
    const val = ev.target ? ev.target.value : null;
    const date = moment().format("YYYY-MM-DD");
    if (val) {
      this.props.change(`line_items[${0}].${field}`, date);
    }
  };

  allItemsSelected = values =>
    isEqual(
      this.state.itemsSelected.sort(),
      values.line_items.map(l => l.id).sort()
    );

  selectAll = values => {
    if (this.allItemsSelected(values)) {
      this.setState({ itemsSelected: [] });
    } else {
      this.setState({
        itemsSelected: values.line_items.map(l => l.id)
      });
    }
  };

  approvalStatuses = Object.keys(
    removeKeys(APPROVAL_STATUSES, [
      REPAIR_EVALUATION_REJECTED,
      REPAIR_EVALUATION_APPROVED
    ])
  );

  search = debounce((updateValues, val) => {
    api.repairOrders.lineItems
      .search(this.props.repairOrder.id, {
        limit: 15,
        filter: val,
        approval_status: this.approvalStatuses.join(","),
        shipped: true,
        simple_presenter: true
      })
      .then(({ data }) => {
        updateValues({ line_items: data.results });
        return function cleanup() {};
      });
  }, 500);

  render() {
    const { initialValues, onSubmit } = this.props;

    const {
      itemsSelected,
      topLevelApprovalStatus,
      approvalStatus
    } = this.state;

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {({ isSubmitting, values, setSubmitting, handleSubmit, ...rest }) => (
          <AppForm handleSubmit={onSubmit} close={this.props.close}>
            <AppForm.Body>
              <FormRow>
                <Col md={2}>
                  <label htmlFor={APPROVAL_STATUS}>
                    {properCase(APPROVAL_STATUS)}
                  </label>
                  <select
                    className="form-control"
                    onChange={this.handleTopLevelFormChange(
                      APPROVAL_STATUS,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                  >
                    {toOptions(
                      removeKeys(APPROVAL_STATUSES, [
                        AWAITING_TO_SHIP,
                        UNIT_RETURNED_TO_CUSTOMER
                      ])
                    ).map(r => (
                      <option key={r.value} value={r.value}>
                        {r.text}
                      </option>
                    ))}
                  </select>
                </Col>
                <Col md={3}>
                  <label htmlFor={TAG_DATE}>
                    {properCase(TAG_DATE)}
                    {approvalStatus === REPAIR_EVALUATION_APPROVED && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <input
                    onChange={this.handleTopLevelFormChange(
                      TAG_DATE,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                    className="form-control"
                    name={TAG_DATE}
                    type="month"
                  />
                </Col>
              </FormRow>
              <FormRow>
                <Col md={2}>
                  <label htmlFor={ORIGINAL_EVALUATION}>
                    {properCase(ORIGINAL_EVALUATION)}
                    {approvalStatus === REPAIR_EVALUATION_APPROVED && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <input
                    onChange={this.handleTopLevelFormChange(
                      ORIGINAL_EVALUATION,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                    className="form-control"
                    name={ORIGINAL_EVALUATION}
                    type="number"
                  />
                </Col>
                <Col md={2}>
                  <label htmlFor={PART_COST}>
                    {properCase(PART_COST)}
                    {approvalStatus === REPAIR_EVALUATION_APPROVED && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <input
                    onChange={this.handleTopLevelFormChange(
                      PART_COST,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                    className="form-control"
                    name={PART_COST}
                    type="number"
                  />
                </Col>
                <Col md={2}>
                  <label htmlFor={LABOR_COST}>
                    {properCase(LABOR_COST)}
                    {approvalStatus === REPAIR_EVALUATION_APPROVED && (
                      <span style={{ color: "red" }}>*</span>
                    )}
                  </label>
                  <input
                    onChange={this.handleTopLevelFormChange(
                      LABOR_COST,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                    className="form-control"
                    name={LABOR_COST}
                    type="number"
                  />
                </Col>
                {approvalStatus === REPAIR_EVALUATION_APPROVED && (
                  <Col md={2}>
                    <label htmlFor={TRANSFER_COST}>
                      {properCase(TRANSFER_COST)}
                    </label>
                    <input
                      onChange={this.handleTopLevelFormChange(
                        TRANSFER_COST,
                        values,
                        initialValues,
                        rest.setFieldValue
                      )}
                      className="form-control"
                      name={TRANSFER_COST}
                      type="number"
                      defaultValue="0.00"
                    />
                  </Col>
                )}
                <Col md={2}>
                  <label htmlFor={ESTIMATED_LEAD_TIME}>
                    {properCase(ESTIMATED_LEAD_TIME)}
                  </label>
                  <input
                    onChange={this.handleTopLevelFormChange(
                      ESTIMATED_LEAD_TIME,
                      values,
                      initialValues,
                      rest.setFieldValue
                    )}
                    className="form-control"
                    name={ESTIMATED_LEAD_TIME}
                    type="number"
                  />
                </Col>
                {topLevelApprovalStatus === REPAIR_EVALUATION_APPROVED && (
                  <Col md={2}>
                    <label htmlFor={CONDITION}>Inventory Condition</label>
                    <select
                      className="form-control"
                      onChange={this.handleTopLevelFormChange(
                        "inventory.condition",
                        values,
                        initialValues,
                        rest.setFieldValue
                      )}
                    >
                      <option value="">Select</option>
                      {toOptions(CONDITIONS).map(r => (
                        <option key={r.value} value={r.value}>
                          {r.text}
                        </option>
                      ))}
                    </select>
                  </Col>
                )}
                {approvalStatus === REPAIR_EVALUATION_REJECTED && (
                  <Col md={2}>
                    <label htmlFor={EVALUATION_FEE}>
                      {properCase(EVALUATION_FEE)}
                    </label>
                    <input
                      onChange={this.handleTopLevelFormChange(
                        EVALUATION_CHARGE,
                        values,
                        initialValues,
                        rest.setFieldValue
                      )}
                      className="form-control"
                      name={EVALUATION_CHARGE}
                      type="number"
                    />
                  </Col>
                )}
              </FormRow>
              <FormRow>
                <Col md={6} style={{ paddingTop: 10 }}>
                  <Warning>
                    Note: Displaying only results for parts not already approved
                    or rejected.
                  </Warning>
                </Col>
              </FormRow>
              <FormRow>
                <Col md={6}>
                  <label htmlFor="Search by PN or SN">Search by PN or SN</label>
                  <input
                    placeholder="Search by PN or SN"
                    className="form-control"
                    onChange={ev =>
                      this.search(rest.setValues, ev.target.value)}
                  />
                </Col>
              </FormRow>
              <hr />
              <br />
              <input
                name="line_items"
                type="checkbox"
                onClick={() => this.selectAll(values)}
                checked={
                  itemsSelected.length > 0 && this.allItemsSelected(values)
                }
              />
              &nbsp;&nbsp;Select All <sup>(Limit 15 Per Update)</sup>
              <FieldArray name="line_items">
                {() => (
                  <div className="striped">
                    {values.line_items.map((l, idx) => (
                      <LineRow
                        idx={idx}
                        values={values}
                        initialValues={initialValues}
                        itemsSelected={itemsSelected}
                        setFieldValue={rest.setFieldValue}
                        toggleItem={this.toggleItem(
                          rest.setFieldValue,
                          initialValues
                        )}
                      />
                    ))}
                  </div>
                )}
              </FieldArray>
            </AppForm.Body>
            <FormikFooter
              rest={rest}
              setSubmitting={setSubmitting}
              isSubmitting={isSubmitting}
              isValid={rest.isValid}
              values={{
                line_items: values.line_items.filter(l =>
                  itemsSelected.includes(l.id)
                )
              }}
              handleSubmit={handleSubmit}
              close={this.props.close}
            />
          </AppForm>
        )}
      </Formik>
    );
  }
}

Form.propTypes = {
  change: T.func.isRequired,
  close: T.func.isRequired,
  includeCancelled: T.bool,
  order: T.shape({})
};

export default connect(undefined, (dispatch, ownProps) => ({
  onSubmit: data => {
    dispatch(ownProps.bulkUpdate(data))
      .then(() => {
        ownProps.close();
      })
      .catch(error => {
        toastr.error("Error", error.message);
      });
  }
}))(Form);



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Detail/UpdateEvaluationStatus/Form.jsx